import { useState } from 'react';
import { X, Search } from 'lucide-react';

interface BrandFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectBrand: (brand: string) => void;
}

const POPULAR_BRANDS = ['Nike', 'Adidas', 'HOKA', 'Brooks', 'ASICS', 'New Balance'];

const ALL_BRANDS = [
  'Adidas', 'Allbirds', 'Altra', 'APL', 'ASICS', 'Brooks', 'Diadora',
  'HOKA', 'Inov-8', 'Jordan', 'Kailas', 'La Sportiva', 'Merrell',
  'Mizuno', 'New Balance', 'Nike', 'NNormal', 'NOBULL', 'On',
  'Puma', 'Reebok', 'Salomon', 'Saucony', 'Scarpa', 'Skechers',
  'The North Face', 'Topo Athletic', 'Under Armour', 'Xero Shoes'
];

export default function BrandFilterModal({ isOpen, onClose, onSelectBrand }: BrandFilterModalProps) {
  const [searchQuery, setSearchQuery] = useState('');

  if (!isOpen) return null;

  const filteredBrands = ALL_BRANDS.filter(brand =>
    brand.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBrandClick = (brand: string) => {
    onSelectBrand(brand);
    onClose();
    setSearchQuery(''); // Reset search on close
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Select a Brand</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-6 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Popular Brands */}
          {!searchQuery && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-600 mb-3">POPULAR BRANDS</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {POPULAR_BRANDS.map(brand => (
                  <button
                    key={brand}
                    onClick={() => handleBrandClick(brand)}
                    className="bg-gradient-to-r from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200 border-2 border-orange-200 hover:border-orange-400 text-orange-700 font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-md"
                  >
                    {brand}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* All Brands */}
          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-3">
              {searchQuery ? `SEARCH RESULTS (${filteredBrands.length})` : 'ALL BRANDS (A-Z)'}
            </h3>
            {filteredBrands.length > 0 ? (
              <div className="space-y-2">
                {filteredBrands.map(brand => (
                  <button
                    key={brand}
                    onClick={() => handleBrandClick(brand)}
                    className="w-full text-left px-4 py-3 hover:bg-orange-50 rounded-lg transition-colors border border-transparent hover:border-orange-200 font-medium text-gray-700 hover:text-orange-700"
                  >
                    {brand}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No brands found matching "{searchQuery}"
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-3 px-6 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
